/**
 * B6 · El camino de niveles — «la sensación de que es un juego por niveles».
 *
 * Serpiente **vertical** de una columna. En móvil el scroll horizontal está
 * prohibido (regla del repo), así que la amplitud de la serpiente se aplica con
 * `transform: translateX(%)` sobre filas que ya caben: el desplazamiento da
 * sensación de camino sin sacar nada del viewport, y el contenedor recorta por
 * si acaso.
 *
 * El conector cuenta el grafo: sólido cuando el prerrequisito es duro,
 * **discontinuo** cuando hay una vía alternativa. Eso es lo que dibuja el salto
 * de nivel sin que este componente sepa qué es un salto — solo pinta las
 * aristas que el servidor derivó de las configuraciones.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Map as MapIcon } from "lucide-solid";
import type { StageLabel } from "@space/shared";
import { cn } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useTrackState } from "./use-progression";
import { type PathRow, toPathRows } from "./use-progression";
import { StageNode } from "./stage-node";

/**
 * Cómo llama ESTA academia a un nivel. El enum viene traducido de fábrica; el
 * texto libre (`custom`) es de la academia y no se traduce a propósito: abrir
 * los 11 locales a texto de usuario sería un agujero de i18n incontrolable.
 */
function stageLabelFor(
  t: ReturnType<typeof useI18n>["t"],
  label: StageLabel,
  custom: string | null | undefined,
  position: number,
): string {
  const n = position + 1;
  switch (label) {
    case "module": {
      return t(msg`Module ${n}`);
    }
    case "cycle": {
      return t(msg`Cycle ${n}`);
    }
    case "term": {
      return t(msg`Term ${n}`);
    }
    case "stage": {
      return t(msg`Stage ${n}`);
    }
    case "custom": {
      return custom ? `${custom} ${n}` : t(msg`Level ${n}`);
    }
    default: {
      return t(msg`Level ${n}`);
    }
  }
}

const Connector: Component<{ link: PathRow["link"] }> = (props) => (
  <Show when={props.link !== "none"}>
    <div
      aria-hidden="true"
      class={cn(
        props.link === "soft" ? "path-link--soft" : "path-link",
        props.link === "done" && "path-link--done",
      )}
    />
  </Show>
);

export const PathMap: Component<{
  trackId: string;
  onOpenStage?: (stageId: string, status: string) => void;
}> = (props) => {
  const { t } = useI18n();
  const state = useTrackState(() => props.trackId);
  const rows = (): PathRow[] => toPathRows(state.data);

  return (
    <div class="w-full overflow-x-hidden" data-testid="progression-path">
      <Show
        when={!state.isLoading}
        fallback={
          <div class="path-track" aria-busy="true">
            <For each={[0, 1, 2]}>{() => <Skeleton class="mb-4 h-24 w-full max-w-xs" />}</For>
          </div>
        }
      >
        <Show
          when={rows().length > 0}
          fallback={
            <EmptyState
              icon={MapIcon}
              title={t(msg`No path yet`)}
              description={t(
                msg`Your academy hasn't published a learning path for this programme yet.`,
              )}
            />
          }
        >
          <Show when={state.data?.prerequisitesEnforced === false}>
            <p class="mb-4 text-center text-sm text-muted-foreground">
              {t(msg`Every level is open right now — your academy turned prerequisites off.`)}
            </p>
          </Show>
          <div class="path-track">
            <For each={rows()}>
              {(row, index) => (
                <>
                  <Connector link={row.link} />
                  <div
                    class={cn(
                      "path-row",
                      row.side === "left" && "path-row--left",
                      row.side === "right" && "path-row--right",
                    )}
                  >
                    <StageNode
                      title={row.title}
                      status={row.status}
                      progressPct={row.progressPct}
                      blockedBy={row.blockedBy}
                      testid={`stage-node-${index()}`}
                      stageLabel={stageLabelFor(
                        t,
                        state.data?.stageLabel ?? "level",
                        state.data?.stageLabelCustom,
                        index(),
                      )}
                      onActivate={() => props.onOpenStage?.(row.stageId, row.status)}
                    />
                  </div>
                </>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
